"use client";

import { ArrowUpRight, CircuitBoard, Cpu, Layers, Radio } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DigitalMarketingServices() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const disciplines = [{
    index: "01",
    title: "Search Engine Optimization",
    description: "Drive organic visibility with data-backed strategies, technical audits, and high-authority content architecture.",
    Icon: CircuitBoard,
    gridClass: "lg:col-start-1 lg:col-span-5 lg:row-span-2"
  }, {
    index: "02",
    title: "Performance Analytics",
    description: "Deep-dive conversion tracking and attribution modeling to ensure every marketing dollar delivers measurable ROI.",
    Icon: Radio,
    gridClass: "lg:col-start-7 lg:col-span-4 lg:row-start-1 lg:mt-16"
  }, {
    index: "03",
    title: "Content Strategy",
    description: "Crafting compelling narratives that resonate with your audience and establish industry authority through precision.",
    Icon: Cpu,
    gridClass: "lg:col-start-2 lg:col-span-5 lg:row-start-3 lg:-mt-8"
  }, {
    index: "04",
    title: "Paid Media Management",
    description: "Scalable ad campaigns across search and social platforms, optimized for maximum reach and conversion efficiency.",
    Icon: Layers,
    gridClass: "lg:col-start-8 lg:col-span-5 lg:row-start-3 lg:-mt-8"
  }];
  return <section data-section-id="2779" id="digital-marketing-services" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 px-6 md:px-10 lg:px-16">
      {}
      <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.07]" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 1200 1200" aria-hidden="true">
        <path d="M 100 200 L 400 200 L 400 450 L 800 450 L 800 700 L 1100 700" stroke="var(--chart-1)" strokeWidth="1" fill="none" />
        <path d="M 200 900 L 600 900 L 600 600 L 1000 600" stroke="var(--chart-1)" strokeWidth="1" fill="none" />
        <circle cx="400" cy="200" r="4" fill="var(--chart-1)" />
        <circle cx="800" cy="450" r="4" fill="var(--chart-1)" />
        <circle cx="600" cy="900" r="4" fill="var(--chart-1)" />
      </svg>

      <div className="relative max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`max-w-3xl mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm uppercase tracking-widest text-muted-foreground mb-6">
            § 02 — expertise
          </p>
          <h2 className="font-serif font-default text-4xl md:text-6xl lg:text-7xl leading-[1.05] text-foreground">
            Core pillars of{" "}
            <span className="italic font-light text-muted-foreground">
              our
            </span>{" "}
            <span className="italic">growth</span>
          </h2>
          <div className="mt-8 h-px w-24 bg-border" />
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {disciplines.map((item, i) => {
          const Icon = item.Icon;
          return <article key={i} data-index={i} className={`group relative bg-card text-card-foreground rounded-lg p-8 md:p-10 border border-border transition-all duration-700 ease-out hover:-translate-y-1 hover:shadow-lg ${item.gridClass} ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: gridVisible ? `${i * 120}ms` : "0ms"
          }}>
                {}
                <div className="flex items-start justify-between mb-12 md:mb-16">
                  <span className="font-mono text-xs tracking-widest text-muted-foreground transition-colors duration-500 group-hover:text-foreground">
                    {item.index} / 04
                  </span>
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border text-muted-foreground transition-transform duration-500 group-hover:rotate-6 group-hover:text-foreground">
                    <Icon className="w-5 h-5" strokeWidth={1.25} />
                  </span>
                </div>

                {}
                <h3 className="font-serif font-default text-2xl md:text-[2rem] leading-tight text-foreground mb-4">
                  {item.title}
                </h3>

                {}
                <p className="font-default text-sm md:text-base text-muted-foreground leading-relaxed mb-10 max-w-md">
                  {item.description}
                </p>

                {}
                <div className="pt-6 border-t border-border">
                  <Button variant="ghost" size="sm" className="px-0 h-auto font-mono text-xs uppercase tracking-widest text-foreground hover:bg-transparent hover:text-secondary group/btn">
                    learn more
                    <ArrowUpRight className="w-3.5 h-3.5 ml-1 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </Button>
                </div>
              </article>;
        })}
        </div>
      </div>
    </section>;
}